import { useState, useRef, useEffect, useCallback } from 'react';
import { Department, sidebarConfigs, departmentOrder } from './sidebar-config';

interface DepartmentDropdownProps {
  activeDepartment: Department;
  onDepartmentChange: (dept: Department) => void;
}

export const DepartmentDropdown: React.FC<DepartmentDropdownProps> = ({
  activeDepartment,
  onDepartmentChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const activeConfig = sidebarConfigs[activeDepartment];

  // Close on click-outside
  useEffect(() => {
    if (!isOpen) return;

    const handleMouseDown = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleSelect = useCallback(
    (dept: Department) => {
      onDepartmentChange(dept);
      setIsOpen(false);
    },
    [onDepartmentChange],
  );

  return (
    <div ref={dropdownRef} className="relative mb-3">
      {/* Trigger */}
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg bg-chrome hover:bg-chrome-muted transition-colors text-left"
      >
        <i className={`fa-solid ${activeConfig.icon} text-accent-light w-5 text-center`} />
        <span className="flex-1 text-sm font-medium text-ink-chrome-muted truncate">
          {activeConfig.name}
        </span>
        <i
          className={`fa-solid fa-chevron-down text-xs text-ink-faint transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Menu */}
      {isOpen && (
        <div className="absolute left-0 right-0 mt-1 py-1 bg-chrome rounded-lg shadow-lg z-50 border border-chrome-muted">
          {departmentOrder.map((dept) => {
            const config = sidebarConfigs[dept];
            const isActive = dept === activeDepartment;

            return (
              <button
                key={dept}
                data-department={dept}
                onClick={() => handleSelect(dept)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${
                  isActive
                    ? 'bg-chrome-muted text-ink-chrome'
                    : 'text-ink-chrome-muted hover:bg-chrome-muted hover:text-ink-chrome'
                }`}
              >
                <i className={`fa-solid ${config.icon} text-accent-light w-5 text-center`} />
                <span className="flex-1 text-sm truncate">{config.name}</span>
                {isActive && (
                  <i className="fa-solid fa-check text-xs text-ink-chrome-muted" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
